import { api } from 'api';
import {
  SettlementDataPut,
  SettlementId,
  settlements,
  SingleSettlementResponse,
} from 'api/endpoints/settlements';
import {
  SettlementAssignedAdmin,
  SettlementAssignedUser,
  SettlementUser,
  SettlementUserRole,
  settlementUsers,
} from 'api/endpoints/settlementUsers';
import { OptionItem } from 'components/ApNiceSelect';
import _, { toInteger } from 'lodash';
import { downloadBlob } from 'utils/file';
import { isNotNil } from 'utils/lodashEx';

export interface UserOptionItem extends OptionItem {
  email: string;
}

export type UserList = {
  counterparty: UserOptionItem[];
  client: UserOptionItem[];
};

export const emptyUserList: UserList = {
  counterparty: [],
  client: [],
};

export type SingleSettlement = SingleSettlementResponse & {
  regenerate: boolean;
  saveComment: string;
  counterpartyId: string | undefined;
  clientId: string | undefined;
  selectedAdminsIds: string[];
};

export type EmailNotification = {
  send: boolean;
  customText?: string;
};

interface SettlementEmailTags {
  date: string;
  claimantName: string;
  userName: string;
}

export const apiSettlementSingle = {
  getUsersList: async () => {
    const allUsers = (await settlementUsers.getAll()) ?? [];
    return {
      counterparty: mapUserToOptions(allUsers, 'Counterparty'),
      client: mapUserToOptions(allUsers, 'Client'),
    };
  },

  getData: async (id: SettlementId) => {
    const [item, users, admin] = await Promise.all([
      settlements.get(id),
      settlementUsers.getAssigned(id),
      settlementUsers.getAssignedAdmin(id),
    ]);

    const result = mapResponseToSingleSettlement(item, users, admin);
    return result;
  },

  update: async (data: SingleSettlement | undefined, email?: EmailNotification) => {
    if (!data) return false;

    const postData = mapSingleSettlementToPut(data, email);
    const result = await settlements.put(data.id, postData, data.regenerate);

    const isSuccess = isNotNil(result);
    return isSuccess;
  },

  agreementDownload: async (id: SettlementId | undefined) => {
    if (!id) return undefined;

    const result = await api.settlements.agreementDownload(id);
    return result.type === 'ok' ? result.data : undefined;
  },

  agreementDownloadFile: async (
    id: SettlementId | undefined,
    fileName: string | null | undefined,
  ) => {
    const blob = await apiSettlementSingle.agreementDownload(id);
    if (!blob) return;

    const agreementFileName = fileName ?? 'agreement.pdf';
    downloadBlob(blob, agreementFileName);
  },

  getEmailPreview: async (emailTags: SettlementEmailTags) => {
    const email = await api.settlements.emailPreview();
    if (!email) return '';

    return email.text
      .trim()
      .replaceAll('{{SettlementDate}}', emailTags.date)
      .replaceAll('{{ClaimantName}}', emailTags.claimantName)
      .replaceAll('{{UserName}}', emailTags.userName)
      .replaceAll('{{OfferProceduresURL}}', '')
      .replaceAll('{{SettlementPortalURL}}', '')
      .replaceAll(/{{.*}}/g, '___');
  },
};

// --------------------------- mappers -----------------------------------
const mapUserToOptions = (users: SettlementUser[], userRole: SettlementUserRole) => {
  const filteredUsers = users.filter((u) => u.userRole === userRole);
  const result: UserOptionItem[] = filteredUsers.map((r) => ({
    label: r.name,
    value: r.id.toString(),
    email: r.email,
  }));
  return result;
};

const mapSingleSettlementToPut = (
  data: SingleSettlement,
  email?: EmailNotification,
) => {
  const settlementData = _.pick(data, [
    'id',
    'counterpartyName',
    'counterpartyEmail',
    'counterpartyPhone',
    'counterpartyCountry',
    'counterpartyState',
    'counterpartyCity',
    'counterpartyZip',
    'counterpartyAddress',
    'status',
    'notes',
    'additionalEmailRecipients',
  ]);

  const settlementUsersAssignment = [data.counterpartyId, data.clientId]
    .filter((r) => r)
    .map(toInteger);

  const result: SettlementDataPut = {
    ...settlementData,
    settlementUsersAssignment,
    settlementAdminsAssignment: data.selectedAdminsIds.map(toInteger),
    emailSend: !!email?.send,
    emailCustomText: email?.customText ?? '',
  };

  return result;
};

const mapResponseToSingleSettlement = (
  item: SingleSettlementResponse | undefined,
  users: SettlementAssignedUser[] | undefined,
  admin: SettlementAssignedAdmin[] | undefined,
) => {
  if (!item) {
    return undefined;
  }

  const result: SingleSettlement = {
    ...item,
    regenerate: false,
    saveComment: '',
    clientId: findUserId(users, 'Client'),
    counterpartyId: findUserId(users, 'Counterparty'),
    selectedAdminsIds: admin?.map((r) => `${r.id}`) ?? [],
  };

  return result;
};

const findUserId = (
  users: SettlementAssignedUser[] | undefined,
  userRole: SettlementUserRole,
) => {
  const result = users?.find((u) => u.role === userRole);
  return result ? `${result.id}` : undefined;
};
